/**
 * SCORM 1.2 API Wrapper
 * Se encarga de buscar el objeto API proporcionado por el LMS (Moodle, Canvas, etc.)
 * y abstrae las llamadas estándar de SCORM.
 */

const ScormAPI = {
    API: null,
    version: '1.2', // '1.2' or '2004'
    isActive: false,
    isInitialized: false,

    /**
     * Busca la API SCORM (1.2 o 2004) en la ventana actual y en sus padres (iframes)
     */
    findAPI(win) {
        let attempts = 0;
        let currentWindow = win;

        try {
            while (currentWindow && currentWindow.API == null && currentWindow.API_1484_11 == null && currentWindow.parent != null && currentWindow.parent !== currentWindow) {
                attempts++;
                if (attempts > 500) {
                    console.warn('SCORM API: Superado el límite máximo de búsqueda en ventanas anidadas.');
                    return null;
                }
                currentWindow = currentWindow.parent;
            }

            if (currentWindow && currentWindow.API_1484_11 != null) {
                this.version = '2004';
                return currentWindow.API_1484_11;
            } else if (currentWindow && currentWindow.API != null) {
                this.version = '1.2';
                return currentWindow.API;
            }
        } catch (e) {
            console.warn('ℹ️ SCORM API: Restricción de acceso entre marcos de origen cruzado (Cross-Origin Frame Access):', e.message);
        }
        return null;
    },

    /**
     * Inicializa la conexión con el LMS (SCORM 1.2 o 2004)
     */
    init() {
        console.log('🔄 SCORM API: Buscando LMS (SCORM 1.2 / SCORM 2004)...');
        this.API = this.findAPI(window);

        if (this.API != null) {
            let result = false;
            if (this.version === '2004') {
                result = this.API.Initialize('');
            } else {
                result = this.API.LMSInitialize('');
            }

            if (result === 'true' || result === true) {
                this.isActive = true;
                this.isInitialized = true;
                console.log(`✅ SCORM API: Conexión con LMS establecida exitosamente (Modo: SCORM ${this.version}).`);
                return true;
            } else {
                const errorInfo = this.getLastError();
                console.error('❌ SCORM API: Error al inicializar LMS.', errorInfo);
                return false;
            }
        } else {
            console.warn('ℹ️ SCORM API: No se detectó ningún LMS. Ejecutando en modo Standalone (Local).');
            return false;
        }
    },

    /**
     * Obtiene un valor del LMS
     */
    get(parameter) {
        if (!this.isActive || !this.API) return '';
        let value = '';
        if (this.version === '2004') {
            const mappedParam = this.mapTo2004(parameter);
            value = this.API.GetValue(mappedParam);
        } else {
            value = this.API.LMSGetValue(parameter);
        }
        return value;
    },

    /**
     * Guarda un valor en el LMS
     */
    set(parameter, value) {
        if (!this.isActive || !this.API) return false;
        let result = false;
        if (this.version === '2004') {
            const mappedParam = this.mapTo2004(parameter);
            result = this.API.SetValue(mappedParam, value);
        } else {
            result = this.API.LMSSetValue(parameter, value);
        }
        return result === 'true' || result === true;
    },

    /**
     * Fuerza la sincronización de datos con el servidor del LMS
     */
    commit() {
        if (!this.isActive || !this.API) return false;
        let result = false;
        if (this.version === '2004') {
            result = this.API.Commit('');
        } else {
            result = this.API.LMSCommit('');
        }
        return result === 'true' || result === true;
    },

    /**
     * Cierra la sesión SCORM con el LMS
     */
    finish() {
        if (!this.isActive || !this.API) return false;
        let result = false;
        if (this.version === '2004') {
            result = this.API.Terminate('');
        } else {
            result = this.API.LMSFinish('');
        }

        if (result === 'true' || result === true) {
            this.isActive = false;
            console.log('✅ SCORM API: Sesión finalizada y sincronizada con el LMS.');
            return true;
        }
        return false;
    },

    /**
     * Traduce parámetros de SCORM 1.2 a SCORM 2004 cuando corresponde
     */
    mapTo2004(param) {
        const mappings = {
            'cmi.core.lesson_status': 'cmi.completion_status',
            'cmi.core.score.raw': 'cmi.score.raw',
            'cmi.core.score.min': 'cmi.score.min',
            'cmi.core.score.max': 'cmi.score.max',
            'cmi.core.lesson_location': 'cmi.location',
            'cmi.core.session_time': 'cmi.session_time',
            'cmi.suspend_data': 'cmi.suspend_data'
        };
        return mappings[param] || param;
    },

    /**
     * Obtiene la descripción del último error ocurrido
     */
    getLastError() {
        if (!this.API) return 'No API';
        if (this.version === '2004') {
            const code = this.API.GetLastError();
            const msg = this.API.GetErrorString(code);
            const diag = this.API.GetDiagnostic(code);
            return { code, message: msg, diagnostic: diag };
        } else {
            const code = this.API.LMSGetLastError();
            const msg = this.API.LMSGetErrorString(code);
            const diag = this.API.LMSGetDiagnostic(code);
            return { code, message: msg, diagnostic: diag };
        }
    }
};

if (typeof window !== 'undefined') {
    window.Detectimaticas = window.Detectimaticas || {};
    window.Detectimaticas.ScormAPI = ScormAPI;
    window.ScormAPI = ScormAPI;
}
export default ScormAPI;
